/**
 * Express Auth Adapter — Calls self-hosted JWT auth endpoints
 * Sprint 3 / Sprint 6: Activation
 *
 * Replaces supabase.auth with fetch calls to Express /api/auth/*.
 */

import type { AuthProvider, AuthStateChangeCallback } from './types';
import { marcarSessaoExpirada } from '@/lib/api/session-guard';

const API_URL = import.meta.env?.VITE_API_URL || 'http://localhost:3001';
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

class ExpressAuthAdapter implements AuthProvider {
  private listeners: Set<AuthStateChangeCallback> = new Set();

  async getSession() {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      return { data: { session: null }, error: null };
    }

    try {
      const res = await fetch(`${API_URL}/api/auth/session`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!res.ok) {
        // HAVIA um token e o servidor recusou: a sessão venceu. Sem este
        // bilhete o usuário cai no login sem explicação e conclui que o
        // sistema "deslogou sozinho" — foi exatamente o que aconteceu.
        marcarSessaoExpirada();
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        return { data: { session: null }, error: null };
      }

      const { data } = await res.json();
      const rawUser = data.session.user;
      // Normalizar: garantir que 'id' existe (AuthContext espera user.id)
      const user = { ...rawUser, id: rawUser.id || rawUser.userId };
      // Atualizar localStorage com dados frescos
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      return {
        data: {
          session: {
            access_token: token,
            user,
          },
        },
        error: null,
      };
    } catch {
      return { data: { session: null }, error: null };
    }
  }

  async getUser() {
    const { data } = await this.getSession();
    if (data.session?.user) {
      return { data: { user: data.session.user }, error: null };
    }
    return { data: { user: null }, error: null };
  }

  async signInWithPassword(credentials: { email: string; password: string }) {
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      const result = await res.json();

      if (res.status === 429) {
        // #1987: limitador de tentativas (server/src/middleware/rate-limit.ts)
        // — a mensagem genérica de erro ("Login failed") não dizia por que a
        // tela recusava. `Retry-After` (segundos) vem sempre que o limitador
        // bloqueia; sem ele (defensivo), mensagem sem prazo.
        const retryAfterSec = Number(res.headers.get('Retry-After'));
        const minutos = Number.isFinite(retryAfterSec) && retryAfterSec > 0
          ? Math.ceil(retryAfterSec / 60)
          : null;
        const message = minutos
          ? `Muitas tentativas. Aguarde ${minutos} minuto${minutos > 1 ? 's' : ''} e tente novamente.`
          : 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
        return { data: { session: null }, error: { message } };
      }

      if (!res.ok || result.error) {
        return { data: { session: null }, error: result.error || { message: 'Login failed' } };
      }

      const session = result.data.session;
      // Normalizar: garantir que 'id' existe
      session.user = { ...session.user, id: session.user.id || session.user.userId };

      // Persist token
      localStorage.setItem(TOKEN_KEY, session.access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(session.user));

      // Notify listeners
      this.notifyListeners('SIGNED_IN', session);

      return { data: { session }, error: null };
    } catch (error: any) {
      return { data: { session: null }, error: { message: error.message || 'Network error' } };
    }
  }

  async signOut(_options?: { scope?: 'local' | 'global' }) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this.notifyListeners('SIGNED_OUT', null);
    return { error: null };
  }

  onAuthStateChange(callback: AuthStateChangeCallback) {
    this.listeners.add(callback);

    // Check initial state
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      const userStr = localStorage.getItem(USER_KEY);
      const user = userStr ? JSON.parse(userStr) : null;
      if (user) {
        setTimeout(() => callback('SIGNED_IN', { access_token: token, user }), 0);
      }
    }

    return {
      data: {
        subscription: {
          unsubscribe: () => {
            this.listeners.delete(callback);
          },
        },
      },
    };
  }

  // updateUser — REMOVIDA (#2007, @security MEDIUM). Chamava a rota
  // `/api/auth/update-password`, removida no mesmo ciclo por ser fluxo morto
  // (ver server/src/routes/auth.ts). Troca de senha é pela gestão.

  private notifyListeners(event: any, session: any) {
    for (const cb of this.listeners) {
      try { cb(event, session); } catch {}
    }
  }
}

export const expressAuthAdapter = new ExpressAuthAdapter();

/**
 * Lê `recursos` do usuário já carregado em localStorage (por login ou
 * getSession), SEM chamada de rede. Existe para checagens síncronas fora de
 * componente React (ex.: `crmLayoutEmitterLigado`, #1872) — o caminho normal
 * (`useAuth()`) é um hook e não serve fora de render.
 *
 * Sessão ausente, corrompida ou de antes do campo existir: `null`, nunca
 * lança. Quem chama trata ausência como "sem o recurso", não como erro.
 */
export function getCachedUserResources(): Record<string, boolean> | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const user = JSON.parse(raw);
    return user?.recursos ?? null;
  } catch {
    return null;
  }
}
